import type { Telemetry } from "@workspace/telemetry-sdk";
import type {
  ISender,
  TelemetryContext
} from "@workspace/telemetry-sdk/dist/interfaces";

/** Mandatory Evo adoption event, emitted once per app load. */
export const ADOPTION_LOADED_EVENT = "loaded";

const ADOPTION_CONTEXT_TYPE = "ADO";
const ADOPTION_APP_MANIFEST_ID = "WorkItems";
const ADOPTION_CORRELATION_ID = "HOSP.Collins.UI";
const ADOPTION_REGION = "UK";
const MAX_BATCH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE_BYTES = 32_768;
const MAX_QUEUED_EVENTS = 50;
const RETRYABLE_STATUSES = new Set([401, 408, 429, 500, 503]);

export interface AdoptionTelemetryIdentity {
  userId?: string;
  organisationId?: string;
  applicationId?: string;
}

export interface AdoptionTelemetryConfig {
  endpointUrl: string;
  getToken: () => Promise<string | undefined>;
  onUnauthorised?: () => void;
}

export interface AdoptionTelemetryClient {
  start: (
    context: TelemetryContext,
    config: AdoptionTelemetryConfig
  ) => Promise<void>;
  track: (eventName: string) => void;
}

type TelemetrySdkLoader = () => Promise<typeof Telemetry>;

export class AdoptionTelemetrySendError extends Error {
  readonly shouldRetry: boolean;

  constructor(shouldRetry: boolean) {
    super("Adoption telemetry request failed");
    this.name = "AdoptionTelemetrySendError";
    this.shouldRetry = shouldRetry;
  }
}

/** Returns null when any identity field required by Evo is missing. */
export const buildAdoptionTelemetryContext = (
  identity: AdoptionTelemetryIdentity,
  sessionId: string
): TelemetryContext | null => {
  const { userId, organisationId, applicationId } = identity;
  if (!userId || !organisationId || !applicationId || !sessionId) return null;

  return {
    type: ADOPTION_CONTEXT_TYPE,
    applicationId,
    appManifestId: ADOPTION_APP_MANIFEST_ID,
    acid: userId,
    org_id: organisationId,
    sid: sessionId,
    sub: "",
    region: ADOPTION_REGION
  };
};

/**
 * Replaces the SDK's default XHR sender, which logs every payload
 * (including user identifiers) to the console.
 */
export const createAdoptionTelemetrySender = (
  config: AdoptionTelemetryConfig
): ISender => ({
  send: async (payload: string[]): Promise<string> => {
    const token = await config.getToken();
    if (!token) return "no token";

    const response = await fetch(config.endpointUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: `[${payload.join(",")}]`,
      keepalive: true
    });

    if (response.ok) return "sent";
    if (response.status === 401) config.onUnauthorised?.();

    throw new AdoptionTelemetrySendError(
      RETRYABLE_STATUSES.has(response.status)
    );
  }
});

const loadTelemetrySdk: TelemetrySdkLoader = async () =>
  (await import("@workspace/telemetry-sdk")).Telemetry;

/**
 * Evo adoption telemetry client. Events tracked before `start` resolves are
 * queued and flushed after `loaded`; every failure is swallowed so telemetry
 * can never affect the app.
 */
export const createAdoptionTelemetryClient = (
  loadSdk: TelemetrySdkLoader = loadTelemetrySdk
): AdoptionTelemetryClient => {
  let telemetry: Telemetry | null = null;
  let hasStarted = false;
  let isDisabled = false;
  let queuedEvents: string[] = [];

  const sendEvent = (eventName: string) => {
    try {
      telemetry?.trackEvent({
        name: eventName,
        payload: { correlationId: ADOPTION_CORRELATION_ID }
      });
    } catch {
      // Adoption telemetry is best-effort and must never surface errors.
    }
  };

  const track = (eventName: string) => {
    if (isDisabled) return;

    if (telemetry) {
      sendEvent(eventName);
      return;
    }

    if (queuedEvents.length < MAX_QUEUED_EVENTS) {
      queuedEvents.push(eventName);
    }
  };

  const flushQueuedEvents = () => {
    const pendingEvents = queuedEvents;
    queuedEvents = [];
    pendingEvents.forEach(sendEvent);
  };

  const start = async (
    context: TelemetryContext,
    config: AdoptionTelemetryConfig
  ): Promise<void> => {
    if (hasStarted) return;
    hasStarted = true;

    try {
      const TelemetrySdk = await loadSdk();
      telemetry = new TelemetrySdk(
        context,
        {
          endpointUrl: config.endpointUrl,
          bearerToken: config.getToken,
          batchSize: MAX_BATCH_SIZE_BYTES,
          maxBatchInterval: MAX_BATCH_INTERVAL_MS
        },
        createAdoptionTelemetrySender(config)
      );
    } catch {
      telemetry = null;
      isDisabled = true;
      queuedEvents = [];
      return;
    }

    sendEvent(ADOPTION_LOADED_EVENT);
    flushQueuedEvents();
  };

  return { start, track };
};

export const adoptionTelemetry = createAdoptionTelemetryClient();
